import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ArrowLeft, Loader2, RefreshCw, Search } from 'lucide-react'
import { RootState } from '../store'
import { isAdminUser } from '../utils/admin'
import { useTranslation } from '../hooks/use-i18n'
import {
  getMissingRoomPredictions,
  type MissingPredictionsReport,
} from '../services/adminInsightsService'

function formatKickoff(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const AdminMissingPredictions: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [report, setReport] = useState<MissingPredictionsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getMissingRoomPredictions(48)
      setReport(data)
    } catch {
      setError(t('adminMissing.error'))
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (!isAdminUser(user)) {
      navigate('/dashboard', { replace: true })
      return
    }
    load()
  }, [user, navigate, load])

  const filteredRooms = useMemo(() => {
    if (!report) return []
    const q = query.trim().toLowerCase()
    if (!q) return report.rooms
    return report.rooms
      .map((room) => ({
        ...room,
        players: room.players.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.email.toLowerCase().includes(q) ||
            room.roomName.toLowerCase().includes(q),
        ),
      }))
      .filter((room) => room.players.length > 0)
  }, [report, query])

  if (!user || !isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-4 py-3 flex items-center gap-4">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" /> {t('nav.dashboard')}
        </Link>
        <h1 className="text-lg font-semibold text-foreground">{t('adminMissing.title')}</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-muted disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {t('adminMissing.refresh')}
        </button>
      </header>

      <main className="container max-w-3xl mx-auto p-4 space-y-4">
        <p className="text-sm text-foreground/70">{t('adminMissing.subtitle')}</p>

        {report && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <div className="text-xl font-bold text-foreground">{report.summary.roomsWithMissing}</div>
              <div className="text-xs text-muted-foreground">{t('adminMissing.rooms')}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <div className="text-xl font-bold text-foreground">{report.summary.playersWithMissing}</div>
              <div className="text-xs text-muted-foreground">{t('adminMissing.players')}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <div className="text-xl font-bold text-foreground">{report.summary.missingPredictions}</div>
              <div className="text-xs text-muted-foreground">{t('adminMissing.missing')}</div>
            </div>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('adminMissing.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-foreground text-sm"
          />
        </div>

        {loading && !report && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        {!loading && report && filteredRooms.length === 0 && (
          <p className="text-sm text-foreground/70 text-center py-8">{t('adminMissing.empty')}</p>
        )}

        {filteredRooms.map((room) => (
          <section key={room.roomId} className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted/30">
              <h2 className="font-semibold text-foreground">{room.roomName}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {room.players.length} {t('adminMissing.players').toLowerCase()}
              </p>
            </div>
            <ul className="divide-y divide-border">
              {room.players.map((player) => (
                <li key={`${room.roomId}-${player.userId}`} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-2">
                    <span className="font-medium text-foreground">{player.name}</span>
                    <a
                      href={`mailto:${player.email}`}
                      className="text-sm text-primary hover:underline break-all"
                    >
                      {player.email}
                    </a>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {player.missingMatches.length} {t('adminMissing.games')}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {player.missingMatches.map((m) => (
                      <li key={m.matchId} className="text-sm text-foreground/80 flex flex-wrap gap-x-2">
                        <span className="font-medium">
                          {m.teamA} {t('news.vs')} {m.teamB}
                        </span>
                        <span className="text-muted-foreground">{formatKickoff(m.kickoff)}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>
    </div>
  )
}

export default AdminMissingPredictions
